"use client";

import { useEffect, useId, useRef, useState } from "react";
import Icon from "./icon";

const itemClassName =
  "flex w-full items-center gap-3 px-4 py-3 text-left text-sm whitespace-nowrap text-black-500 hover:bg-sand-800 focus-visible:bg-sand-800 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-black-500";

// Fast ruta så att alla ikoner tar lika stor plats i sidled och texterna
// linjerar, oavsett ikonens egna mått.
function IconBox({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-5 shrink-0 items-center justify-center [&>svg]:size-full"
    >
      {children}
    </span>
  );
}

function ShareButtons(props: {
  title: string;
  url: string;
  summary?: string;
  shareText?: string;
}) {
  const title = props.title;
  const url =
    props.url || (typeof window !== "undefined" ? window.location.href : "");
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Sätts efter mount så att servern och första klientrenderingen stämmer
  // överens; visar systemets delningsruta (där t.ex. Instagram finns) på
  // enheter som stöder det.
  const [canNativeShare, setCanNativeShare] = useState(false);
  useEffect(() => {
    setCanNativeShare(typeof navigator.share === "function");
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const close = (returnFocus: boolean) => {
    setOpen(false);
    setCopied(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(true);
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      // Stays visible (no auto-dismiss) so users who need more time to
      // notice it, e.g. screen magnifier users, don't miss it.
      setCopied(true);
    } catch (err) {
      console.error("Kunde inte kopiera länken:", err);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ url, title });
      close(false);
    } catch (err) {
      // AbortError när användaren stänger delningsrutan; menyn får stå kvar.
      if ((err as Error).name !== "AbortError") {
        console.error("Delning misslyckades:", err);
      }
    }
  };

  if (!props.url) return null;

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const links = [
    {
      label: "Dela på Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&t=${encodedTitle}`,
      icon: (
        <IconBox>
          <Icon.Facebook />
        </IconBox>
      ),
    },
    {
      label: "Dela på X",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: (
        <IconBox>
          <Icon.Twitter />
        </IconBox>
      ),
    },
    {
      label: "Dela på LinkedIn",
      href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
      icon: (
        <IconBox>
          <Icon.LinkedIn />
        </IconBox>
      ),
    },
  ];

  return (
    <div ref={containerRef} className="relative inline-flex ml-auto">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className="btn p-0 rounded-sm hover:bg-sand-800 bg-sand-700"
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <span className="h-full bg-sand-800 py-2 px-2 rounded-sm md:rounded-none md:rounded-l-sm">
          <Icon.Share
            aria-hidden="true"
            className="size-[18px] fill-black-500 text-black-500"
          />
        </span>
        <span className="hidden md:block px-2">Dela</span>
        <span className="sr-only md:hidden">Dela</span>
      </button>
      {open && (
        <div
          id={menuId}
          className="absolute right-0 top-full z-10 mt-1 flex min-w-full flex-col overflow-hidden rounded-sm border border-sand-900 bg-white-500 shadow-md"
        >
          <button type="button" className={itemClassName} onClick={copyLink}>
            <IconBox>
              {copied ? (
                <Icon.ClipboardDocumentCheck />
              ) : (
                <Icon.ClipboardDocument />
              )}
            </IconBox>
            <span>{copied ? "Länk kopierad" : "Kopiera länk"}</span>
          </button>
          {links.map((link) => (
            <a
              key={link.label}
              className={`${itemClassName} border-t border-sand-900`}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => close(false)}
              // Länkar aktiveras bara med Enter som standard; menyvalen
              // ska bete sig som knappar och även svara på mellanslag.
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                  e.currentTarget.click();
                }
              }}
            >
              {link.icon}
              <span>{link.label}</span>
              <span className="sr-only"> (öppnas i ny flik)</span>
            </a>
          ))}
          {canNativeShare && (
            <button
              type="button"
              className={`${itemClassName} border-t border-sand-900`}
              onClick={nativeShare}
            >
              <IconBox>
                <Icon.Ellipsis />
              </IconBox>
              <span>Fler sätt att dela…</span>
            </button>
          )}
          <span role="status" aria-live="polite" className="sr-only">
            {copied ? "Länk kopierad" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

export default ShareButtons;
